import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, X, HardHat,
  Users, MapPin, Clock
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function PlanningPage() {
  const { user } = useOutletContext();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [chantiers, setChantiers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignData, setAssignData] = useState({
    planned_start_date: '',
    planned_end_date: '',
    assigned_team: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const [chantiersRes, teamsRes] = await Promise.all([
        fetch(`${API_URL}/api/batiment/chantiers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/batiment/teams`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (chantiersRes.ok) setChantiers(await chantiersRes.json());
      if (teamsRes.ok) setTeams(await teamsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTeam = async () => {
    if (!selectedChantier) return;
    const token = localStorage.getItem('client_token');

    try {
      const params = new URLSearchParams();
      if (assignData.planned_start_date) params.append('planned_start_date', assignData.planned_start_date);
      if (assignData.planned_end_date) params.append('planned_end_date', assignData.planned_end_date);
      if (assignData.assigned_team) params.append('assigned_team', assignData.assigned_team);

      const response = await fetch(
        `${API_URL}/api/batiment/chantiers/${selectedChantier.id}?${params.toString()}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Chantier mis à jour');
        setShowAssignModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Get first day of week (Monday = 0)
    let startDay = firstDay.getDay() - 1;
    if (startDay === -1) startDay = 6;

    // Add days from previous month
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    // Add days from next month
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const isChantierOnDate = (chantier, date) => {
    if (!chantier.planned_start_date) return false;
    const start = new Date(chantier.planned_start_date);
    const end = chantier.planned_end_date ? new Date(chantier.planned_end_date) : start;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return d >= start && d <= end;
  };

  const getTeamColor = (teamName) => {
    const team = teams.find(t => t.name === teamName);
    return team?.color || '#6B7280';
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const today = () => {
    setCurrentDate(new Date());
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const days = getDaysInMonth(currentDate);
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  // Unplanned chantiers
  const unplannedChantiers = chantiers.filter(c => !c.planned_start_date && c.status !== 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="planning-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Planification</h1>
          <p className="text-rm-text-secondary">Organisez tes chantiers et équipes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={today}>
            Aujourd'hui
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-slate-100">
          {/* Calendar Header */}
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-rm-text">
              {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Days Header */}
          <div className="grid grid-cols-7 border-b border-slate-100">
            {DAYS.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-rm-text-secondary">
                {day}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7">
            {days.map((day, index) => {
              const isToday = day.date.getTime() === todayDate.getTime();
              const dayChantiers = chantiers.filter(c => isChantierOnDate(c, day.date));

              return (
                <div
                  key={index}
                  className={`min-h-[100px] p-2 border-b border-r border-slate-100 ${
                    !day.isCurrentMonth ? 'bg-slate-50' : ''
                  } ${isToday ? 'bg-blue-50' : ''}`}
                >
                  <p className={`text-sm mb-1 ${
                    !day.isCurrentMonth ? 'text-slate-400' : 
                    isToday ? 'text-blue-600 font-bold' : 'text-rm-text'
                  }`}>
                    {day.date.getDate()}
                  </p>
                  <div className="space-y-1">
                    {dayChantiers.slice(0, 3).map(chantier => (
                      <div
                        key={chantier.id}
                        onClick={() => {
                          setSelectedChantier(chantier);
                          setShowDetailModal(true);
                        }}
                        className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                        style={{ 
                          backgroundColor: getTeamColor(chantier.assigned_team) + '20',
                          borderLeft: `3px solid ${getTeamColor(chantier.assigned_team)}`
                        }}
                      >
                        {chantier.client_name}
                      </div>
                    ))}
                    {dayChantiers.length > 3 && (
                      <p className="text-xs text-slate-400 text-center">
                        +{dayChantiers.length - 3}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar - Unplanned */}
        <div className="space-y-4">
          {/* Teams Legend */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
            <h3 className="font-semibold text-rm-text mb-3">Équipes</h3>
            {teams.length === 0 ? (
              <p className="text-sm text-rm-text-secondary">Aucune équipe créée</p>
            ) : (
              <div className="space-y-2">
                {teams.map(team => (
                  <div key={team.id} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: team.color }}
                    />
                    <span className="text-sm">{team.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unplanned Chantiers */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
            <h3 className="font-semibold text-rm-text mb-3">À planifier</h3>
            {unplannedChantiers.length === 0 ? (
              <p className="text-sm text-rm-text-secondary">Tous les chantiers sont planifiés</p>
            ) : (
              <div className="space-y-2">
                {unplannedChantiers.map(chantier => (
                  <div
                    key={chantier.id}
                    onClick={() => {
                      setSelectedChantier(chantier);
                      setAssignData({
                        planned_start_date: '',
                        planned_end_date: '',
                        assigned_team: chantier.assigned_team || ''
                      });
                      setShowAssignModal(true);
                    }}
                    className="p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
                  >
                    <p className="font-medium text-sm">{chantier.client_name}</p>
                    <p className="text-xs text-amber-700 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {chantier.address || 'Adresse non définie'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedChantier && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-rm-text">{selectedChantier.client_name}</h2>
                    <p className="text-rm-text-secondary text-sm">{selectedChantier.address}</p>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-rm-text-secondary">Début prévu</span>
                    <p className="font-medium">{formatDate(selectedChantier.planned_start_date) || 'Non planifié'}</p>
                  </div>
                  <div>
                    <span className="text-rm-text-secondary">Fin prévue</span>
                    <p className="font-medium">{formatDate(selectedChantier.planned_end_date) || '-'}</p>
                  </div>
                  <div>
                    <span className="text-rm-text-secondary">Équipe</span>
                    <p className="font-medium">{selectedChantier.assigned_team || 'Non assignée'}</p>
                  </div>
                  <div>
                    <span className="text-rm-text-secondary">Statut</span>
                    <p className="font-medium capitalize">{selectedChantier.status?.replace('_', ' ')}</p>
                  </div>
                </div>

                {selectedChantier.work_description && (
                  <div>
                    <span className="text-rm-text-secondary text-sm">Description</span>
                    <p className="text-sm mt-1">{selectedChantier.work_description}</p>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDetailModal(false);
                    setAssignData({
                      planned_start_date: selectedChantier.planned_start_date?.split('T')[0] || '',
                      planned_end_date: selectedChantier.planned_end_date?.split('T')[0] || '',
                      assigned_team: selectedChantier.assigned_team || ''
                    });
                    setShowAssignModal(true);
                  }}
                >
                  Modifier
                </Button>
                <Button onClick={() => setShowDetailModal(false)}>Fermer</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign Modal */}
      <AnimatePresence>
        {showAssignModal && selectedChantier && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAssignModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-xl font-bold text-rm-text">Planifier le chantier</h2>
                <p className="text-rm-text-secondary text-sm">{selectedChantier.client_name}</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Date de début</label>
                  <input
                    type="date"
                    value={assignData.planned_start_date}
                    onChange={(e) => setAssignData({ ...assignData, planned_start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Date de fin</label>
                  <input
                    type="date"
                    value={assignData.planned_end_date}
                    onChange={(e) => setAssignData({ ...assignData, planned_end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Équipe</label>
                  <select
                    value={assignData.assigned_team}
                    onChange={(e) => setAssignData({ ...assignData, assigned_team: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  >
                    <option value="">Sélectionner une équipe</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.name}>{team.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAssignModal(false)}>Annuler</Button>
                <Button className="bg-rm-primary" onClick={handleAssignTeam}>Enregistrer</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
